import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiClock,
    FiCreditCard,
    FiFileText,
    FiLoader,
    FiPlus,
    FiRefreshCw,
    FiSlash,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    cancelCreditNote,
    confirmCreditNote,
    fetchCreditNotesByInvoice,
    invoiceCreditNotesSelector,
    invoiceCreditNoteSummarySelector,
    creditNoteLoadingSelector,
    creditNoteErrorSelector,
} from "../../../../../../../store/billing/creditNotes/creditNoteSlice";

import CreateCreditNoteDialog from "./CreateCreditNoteDialog";
import CreditNoteDetailsDialog from "./CreditNoteDetailsDialog";

import "./CreditNotesPanel.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) => {
    return (
        Math.round(
            (
                toNumber(
                    value
                ) +
                Number.EPSILON
            ) *
                100
        ) / 100
    );
};

const formatCurrency = (
    value
) => {
    return new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );
};

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(date);
};

const formatStatus = (
    value
) => {
    return String(
        value || "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

const getInvoiceId = (
    invoice
) => {
    return (
        invoice?._id ||
        invoice?.id ||
        ""
    );
};

const getInvoiceStatus = (
    invoice
) => {
    return String(
        invoice?.documentStatus ||
            invoice?.status ||
            ""
    ).toUpperCase();
};

const getCreditAmount = (
    creditNote
) => {
    return roundMoney(
        creditNote
            ?.totals
            ?.creditAmount ??
            creditNote
                ?.creditAmount ??
            0
    );
};

const getErrorMessage = (
    error,
    fallbackMessage
) => {
    return (
        error?.response?.data
            ?.message ||
        error?.payload?.message ||
        error?.message ||
        (
            typeof error ===
            "string"
                ? error
                : ""
        ) ||
        fallbackMessage
    );
};

/*
|--------------------------------------------------------------------------
| STATUS ICON
|--------------------------------------------------------------------------
*/

const CreditNoteStatusIcon = ({
    status,
}) => {
    const normalizedStatus =
        String(
            status || ""
        ).toUpperCase();

    if (
        normalizedStatus ===
        "CONFIRMED"
    ) {
        return (
            <FiCheckCircle />
        );
    }

    if (
        normalizedStatus ===
        "CANCELLED"
    ) {
        return (
            <FiSlash />
        );
    }

    return (
        <FiClock />
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CreditNotesPanel = ({
    invoice,
    onInvoiceRefresh,
}) => {
    const dispatch =
        useDispatch();

    const creditNotes =
        useSelector(
            invoiceCreditNotesSelector
        ) || [];

    const summary =
        useSelector(
            invoiceCreditNoteSummarySelector
        ) || {};

    const loading =
        useSelector(
            creditNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            creditNoteErrorSelector
        ) || {};

    const invoiceId =
        getInvoiceId(
            invoice
        );

    const invoiceStatus =
        getInvoiceStatus(
            invoice
        );

    const [
        createDialogOpen,
        setCreateDialogOpen,
    ] = useState(false);

    const [
        detailsDialogOpen,
        setDetailsDialogOpen,
    ] = useState(false);

    const [
        selectedCreditNote,
        setSelectedCreditNote,
    ] = useState(null);

    const [
        actionError,
        setActionError,
    ] = useState("");

    const [
        actionCreditNoteId,
        setActionCreditNoteId,
    ] = useState("");

    const canCreateCreditNote =
        Boolean(invoiceId) &&
        invoiceStatus ===
            "CONFIRMED";

    /*
    |--------------------------------------------------------------------------
    | LOAD CREDIT NOTES
    |--------------------------------------------------------------------------
    */

    const loadCreditNotes =
        useCallback(
            async () => {
                if (!invoiceId) {
                    return;
                }

                try {
                    setActionError(
                        ""
                    );

                    await dispatch(
                        fetchCreditNotesByInvoice({
                            invoiceId,

                            includeCancelled:
                                true,
                        })
                    ).unwrap();
                } catch (
                    error
                ) {
                    setActionError(
                        getErrorMessage(
                            error,
                            "Unable to load credit notes."
                        )
                    );
                }
            },
            [
                dispatch,
                invoiceId,
            ]
        );

    useEffect(
        () => {
            loadCreditNotes();
        },
        [
            loadCreditNotes,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const confirmedCreditAmount =
        useMemo(
            () => {
                if (
                    summary
                        ?.confirmedCreditAmount !==
                    undefined
                ) {
                    return roundMoney(
                        summary
                            .confirmedCreditAmount
                    );
                }

                return roundMoney(
                    creditNotes.reduce(
                        (
                            total,
                            creditNote
                        ) => {
                            const status =
                                String(
                                    creditNote
                                        ?.status ||
                                        ""
                                ).toUpperCase();

                            if (
                                status !==
                                "CONFIRMED"
                            ) {
                                return total;
                            }

                            return (
                                total +
                                getCreditAmount(
                                    creditNote
                                )
                            );
                        },
                        0
                    )
                );
            },
            [
                creditNotes,
                summary,
            ]
        );

    const confirmedCount =
        summary?.confirmed !==
        undefined
            ? Number(
                  summary.confirmed
              )
            : creditNotes.filter(
                  (
                      creditNote
                  ) =>
                      String(
                          creditNote
                              ?.status ||
                              ""
                      ).toUpperCase() ===
                      "CONFIRMED"
              ).length;

    const draftCount =
        summary?.draft !==
        undefined
            ? Number(
                  summary.draft
              )
            : creditNotes.filter(
                  (
                      creditNote
                  ) =>
                      String(
                          creditNote
                              ?.status ||
                              ""
                      ).toUpperCase() ===
                      "DRAFT"
              ).length;

    /*
    |--------------------------------------------------------------------------
    | DETAILS DIALOG
    |--------------------------------------------------------------------------
    */

    const handleOpenDetails =
        useCallback(
            (
                creditNote
            ) => {
                if (!creditNote) {
                    return;
                }

                setSelectedCreditNote(
                    creditNote
                );

                setDetailsDialogOpen(
                    true
                );
            },
            []
        );

    const handleCloseDetails =
        useCallback(
            () => {
                setDetailsDialogOpen(
                    false
                );

                setSelectedCreditNote(
                    null
                );
            },
            []
        );

    /*
    |--------------------------------------------------------------------------
    | REFRESH AFTER CREATE / CONFIRM / CANCEL
    |--------------------------------------------------------------------------
    */

    const refreshAfterUpdate =
        useCallback(
            async () => {
                await loadCreditNotes();

                if (
                    typeof onInvoiceRefresh ===
                    "function"
                ) {
                    await onInvoiceRefresh();
                }
            },
            [
                loadCreditNotes,
                onInvoiceRefresh,
            ]
        );

    const handleCreated =
        useCallback(
            async () => {
                setCreateDialogOpen(
                    false
                );

                await refreshAfterUpdate();
            },
            [
                refreshAfterUpdate,
            ]
        );

    const handleCreditNoteUpdated =
        useCallback(
            async (
                updatedCreditNote
            ) => {
                if (
                    updatedCreditNote
                ) {
                    setSelectedCreditNote(
                        updatedCreditNote
                    );
                }

                await refreshAfterUpdate();
            },
            [
                refreshAfterUpdate,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | CONFIRM CREDIT NOTE
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        useCallback(
            async (
                creditNote
            ) => {
                const creditNoteId =
                    creditNote?._id;

                if (!creditNoteId) {
                    setActionError(
                        "Credit note ID is missing."
                    );

                    return;
                }

                const approved =
                    window.confirm(
                        `Confirm credit note ${
                            creditNote
                                ?.creditNoteNumber ||
                            ""
                        }?\n\nThis will restore stock and post the customer ledger credit.`
                    );

                if (!approved) {
                    return;
                }

                try {
                    setActionError(
                        ""
                    );

                    setActionCreditNoteId(
                        creditNoteId
                    );

                    await dispatch(
                        confirmCreditNote(
                            creditNoteId
                        )
                    ).unwrap();

                    await refreshAfterUpdate();
                } catch (
                    error
                ) {
                    setActionError(
                        getErrorMessage(
                            error,
                            "Unable to confirm credit note."
                        )
                    );
                } finally {
                    setActionCreditNoteId(
                        ""
                    );
                }
            },
            [
                dispatch,
                refreshAfterUpdate,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | CANCEL DRAFT CREDIT NOTE
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        useCallback(
            async (
                creditNote
            ) => {
                const creditNoteId =
                    creditNote?._id;

                if (!creditNoteId) {
                    setActionError(
                        "Credit note ID is missing."
                    );

                    return;
                }

                const cancellationReason =
                    window.prompt(
                        `Enter cancellation reason for ${
                            creditNote
                                ?.creditNoteNumber ||
                            "this credit note"
                        }:`
                    );

                const normalizedReason =
                    String(
                        cancellationReason ||
                            ""
                    ).trim();

                if (
                    normalizedReason.length <
                    3
                ) {
                    return;
                }

                try {
                    setActionError(
                        ""
                    );

                    setActionCreditNoteId(
                        creditNoteId
                    );

                    await dispatch(
                        cancelCreditNote({
                            creditNoteId,

                            cancellationReason:
                                normalizedReason,
                        })
                    ).unwrap();

                    await loadCreditNotes();
                } catch (
                    error
                ) {
                    setActionError(
                        getErrorMessage(
                            error,
                            "Unable to cancel credit note."
                        )
                    );
                } finally {
                    setActionCreditNoteId(
                        ""
                    );
                }
            },
            [
                dispatch,
                loadCreditNotes,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <section className="credit-notes-panel">
            <header className="credit-notes-panel-header">
                <div className="credit-notes-panel-heading">
                    <span className="credit-notes-panel-heading-icon">
                        <FiCreditCard />
                    </span>

                    <div>
                        <h2>
                            Credit Notes
                        </h2>

                        <p>
                            View sales returns and credits created against this invoice.
                        </p>
                    </div>
                </div>

                <div className="credit-notes-panel-actions">
                    <button
                        type="button"
                        className="credit-notes-panel-button credit-notes-panel-refresh-button"
                        disabled={
                            loading.invoice
                        }
                        onClick={
                            loadCreditNotes
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.invoice
                                    ? "credit-notes-panel-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="credit-notes-panel-button credit-notes-panel-create-button"
                        disabled={
                            !canCreateCreditNote
                        }
                        title={
                            canCreateCreditNote
                                ? "Create credit note"
                                : "Credit note can be created only for a confirmed invoice."
                        }
                        onClick={() =>
                            setCreateDialogOpen(
                                true
                            )
                        }
                    >
                        <FiPlus />

                        Create Credit Note
                    </button>
                </div>
            </header>

            {(actionError ||
                errors.invoice) && (
                <div className="credit-notes-panel-alert">
                    <FiAlertCircle />

                    <span>
                        {actionError ||
                            errors.invoice}
                    </span>
                </div>
            )}

            <div className="credit-notes-panel-summary">
                <div className="credit-notes-panel-summary-card">
                    <span>
                        Total Credit Notes
                    </span>

                    <strong>
                        {
                            creditNotes.length
                        }
                    </strong>
                </div>

                <div className="credit-notes-panel-summary-card">
                    <span>
                        Confirmed
                    </span>

                    <strong>
                        {
                            confirmedCount
                        }
                    </strong>
                </div>

                <div className="credit-notes-panel-summary-card">
                    <span>
                        Draft
                    </span>

                    <strong>
                        {
                            draftCount
                        }
                    </strong>
                </div>

                <div className="credit-notes-panel-summary-card credit-notes-panel-summary-amount">
                    <span>
                        Confirmed Credit
                    </span>

                    <strong>
                        {formatCurrency(
                            confirmedCreditAmount
                        )}
                    </strong>
                </div>
            </div>

            {loading.invoice &&
                creditNotes.length ===
                    0 && (
                    <div className="credit-notes-panel-loading">
                        <FiLoader className="credit-notes-panel-spinner" />

                        Loading credit notes...
                    </div>
                )}

            {!loading.invoice &&
                creditNotes.length ===
                    0 && (
                    <div className="credit-notes-panel-empty">
                        <span className="credit-notes-panel-empty-icon">
                            <FiFileText />
                        </span>

                        <h3>
                            No credit notes
                        </h3>

                        <p>
                            No sales return or credit note has been created for this invoice.
                        </p>

                        {canCreateCreditNote && (
                            <button
                                type="button"
                                onClick={() =>
                                    setCreateDialogOpen(
                                        true
                                    )
                                }
                            >
                                <FiPlus />

                                Create Credit Note
                            </button>
                        )}
                    </div>
                )}

            {creditNotes.length >
                0 && (
                <div className="credit-notes-panel-table-wrapper">
                    <table className="credit-notes-panel-table">
                        <thead>
                            <tr>
                                <th>
                                    Credit Note
                                </th>

                                <th>
                                    Date
                                </th>

                                <th>
                                    Reason
                                </th>

                                <th>
                                    Items
                                </th>

                                <th className="credit-notes-panel-text-right">
                                    Credit Amount
                                </th>

                                <th>
                                    Status
                                </th>

                                <th className="credit-notes-panel-actions-column">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {creditNotes.map(
                                (
                                    creditNote,
                                    index
                                ) => {
                                    const creditNoteId =
                                        creditNote
                                            ?._id;

                                    const status =
                                        String(
                                            creditNote
                                                ?.status ||
                                                "DRAFT"
                                        ).toUpperCase();

                                    const processing =
                                        actionCreditNoteId ===
                                        creditNoteId;

                                    const itemCount =
                                        Array.isArray(
                                            creditNote
                                                ?.items
                                        )
                                            ? creditNote
                                                  .items
                                                  .length
                                            : 0;

                                    return (
                                        <tr
                                            key={
                                                creditNoteId ||
                                                index
                                            }
                                        >
                                            <td>
                                                <button
                                                    type="button"
                                                    className="credit-notes-panel-number-button"
                                                    onClick={() =>
                                                        handleOpenDetails(
                                                            creditNote
                                                        )
                                                    }
                                                >
                                                    {creditNote
                                                        ?.creditNoteNumber ||
                                                        "-"}
                                                </button>

                                                <span className="credit-notes-panel-invoice-number">
                                                    Against{" "}
                                                    {creditNote
                                                        ?.saleInvoiceNumber ||
                                                        "-"}
                                                </span>
                                            </td>

                                            <td>
                                                {formatDate(
                                                    creditNote
                                                        ?.creditNoteDate
                                                )}
                                            </td>

                                            <td>
                                                <strong className="credit-notes-panel-reason">
                                                    {formatStatus(
                                                        creditNote
                                                            ?.reason
                                                    )}
                                                </strong>

                                                {creditNote
                                                    ?.reasonDetails && (
                                                    <span className="credit-notes-panel-reason-details">
                                                        {
                                                            creditNote
                                                                .reasonDetails
                                                        }
                                                    </span>
                                                )}
                                            </td>

                                            <td>
                                                {
                                                    itemCount
                                                }
                                            </td>

                                            <td className="credit-notes-panel-text-right credit-notes-panel-credit-amount">
                                                {formatCurrency(
                                                    getCreditAmount(
                                                        creditNote
                                                    )
                                                )}
                                            </td>

                                            <td>
                                                <span
                                                    className={`credit-notes-panel-status credit-notes-panel-status-${status.toLowerCase()}`}
                                                >
                                                    <CreditNoteStatusIcon
                                                        status={
                                                            status
                                                        }
                                                    />

                                                    {formatStatus(
                                                        status
                                                    )}
                                                </span>
                                            </td>

                                            <td className="credit-notes-panel-actions-column">
                                                <div className="credit-notes-panel-row-actions">
                                                    <button
                                                        type="button"
                                                        className="credit-notes-panel-view-button"
                                                        disabled={
                                                            processing
                                                        }
                                                        onClick={() =>
                                                            handleOpenDetails(
                                                                creditNote
                                                            )
                                                        }
                                                    >
                                                        View
                                                    </button>

                                                    {status ===
                                                        "DRAFT" && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="credit-notes-panel-confirm-button"
                                                                disabled={
                                                                    processing
                                                                }
                                                                onClick={() =>
                                                                    handleConfirm(
                                                                        creditNote
                                                                    )
                                                                }
                                                            >
                                                                {processing ? (
                                                                    <FiLoader className="credit-notes-panel-spinner" />
                                                                ) : (
                                                                    <FiCheckCircle />
                                                                )}

                                                                Confirm
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="credit-notes-panel-cancel-button"
                                                                disabled={
                                                                    processing
                                                                }
                                                                onClick={() =>
                                                                    handleCancel(
                                                                        creditNote
                                                                    )
                                                                }
                                                            >
                                                                <FiSlash />

                                                                Cancel
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateCreditNoteDialog
                open={
                    createDialogOpen
                }
                invoice={
                    invoice
                }
                onClose={() =>
                    setCreateDialogOpen(
                        false
                    )
                }
                onCreated={
                    handleCreated
                }
            />

            <CreditNoteDetailsDialog
                open={
                    detailsDialogOpen
                }
                creditNote={
                    selectedCreditNote
                }
                onClose={
                    handleCloseDetails
                }
                onUpdated={
                    handleCreditNoteUpdated
                }
            />
        </section>
    );
};

export default CreditNotesPanel;